import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { TextArea } from '../ui/TextArea';
import { Checkbox } from '../ui/Checkbox';
import { Button } from '../ui/Button';
import { callBillingAPI } from '../../utils/api';
import { useData } from '../../context/DataContext';
interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  customerId?: string | null;
}
export function EditCustomerModal({
  isOpen,
  onClose,
  onSaved,
  customerId
}: EditCustomerModalProps) {
  const {
    selectedCompanyId,
    selectedCompany
  } = useData();
  const isTKDscore = selectedCompany?.name?.toLowerCase().includes('tkdscore') || false;
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organization: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    terms_accepted: false,
    wholesale_customer: false,
    websites: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const convertTermsAccepted = (value: number | string | boolean): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === '1' || value === 'true';
    return false;
  };
  useEffect(() => {
    if (!isOpen) return;
    if (customerId) {
      // Edit mode
      const fetchCustomerDetails = async () => {
        setIsLoading(true);
        setFormErrors({});
        try {
          const customerDetails = await callBillingAPI('getCustomer', {
            id: customerId
          });
          setFormData({
            first_name: customerDetails.first_name || '',
            last_name: customerDetails.last_name || '',
            email: customerDetails.email || '',
            phone: customerDetails.phone || '',
            organization: customerDetails.organization || '',
            address_street: customerDetails.address_street || '',
            address_city: customerDetails.address_city || '',
            address_state: customerDetails.address_state || '',
            address_zip: customerDetails.address_zip || '',
            terms_accepted: convertTermsAccepted(customerDetails.terms_accepted),
            wholesale_customer: !!(customerDetails as any).wholesale_customer,
            websites: (customerDetails as any).websites || ''
          });
        } catch (error) {
          console.error('Failed to fetch customer details:', error);
          setFormErrors({
            submit: error instanceof Error ? error.message : 'Failed to load customer details'
          });
        } finally {
          setIsLoading(false);
        }
      };
      fetchCustomerDetails();
    } else {
      // Create mode
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        organization: '',
        address_street: '',
        address_city: '',
        address_state: '',
        address_zip: '',
        terms_accepted: false,
        wholesale_customer: false,
        websites: ''
      });
      setFormErrors({});
    }
  }, [isOpen, customerId]);
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    setFormErrors({});
    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        address_street: formData.address_street,
        address_city: formData.address_city,
        address_zip: formData.address_zip,
        address_state: formData.address_state,
        terms_accepted: formData.terms_accepted ? 1 : 0,
        wholesale_customer: formData.wholesale_customer ? 1 : 0,
        websites: formData.websites
      };
      if (customerId) {
        await callBillingAPI('editCustomer', {
          id: customerId,
          ...payload
        });
      } else {
        await callBillingAPI('addCustomer', {
          company_id: selectedCompanyId,
          ...payload
        });
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error('Failed to save customer:', error);
      setFormErrors({
        submit: error instanceof Error ? error.message : 'Failed to save customer'
      });
    } finally {
      setIsSaving(false);
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={customerId ? 'Edit Customer' : 'Add Customer'} size="lg" data-id="element-115">
      {isLoading ? <div className="p-12 text-center" data-id="element-116">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-117"></div>
          <p className="mt-4 text-sm text-gray-500" data-id="element-118">
            Loading customer details...
          </p>
        </div> : <div className="space-y-4" data-id="element-119">
          <div className="grid grid-cols-2 gap-4" data-id="element-120">
            <Input label="First Name *" value={formData.first_name} onChange={e => setFormData({
          ...formData,
          first_name: e.target.value
        })} error={formErrors.first_name} placeholder="John" data-id="element-121" />
            <Input label="Last Name" value={formData.last_name} onChange={e => setFormData({
          ...formData,
          last_name: e.target.value
        })} error={formErrors.last_name} placeholder="Doe" data-id="element-122" />
          </div>

          <Input label="Email *" type="email" value={formData.email} onChange={e => setFormData({
        ...formData,
        email: e.target.value
      })} error={formErrors.email} placeholder="john@example.com" data-id="element-123" />

          <Input label="Phone" type="tel" value={formData.phone} onChange={e => setFormData({
        ...formData,
        phone: e.target.value
      })} error={formErrors.phone} placeholder="555-0100" data-id="element-124" />

          <Input label="Organization" value={formData.organization} onChange={e => setFormData({
        ...formData,
        organization: e.target.value
      })} placeholder="Acme Inc." helperText="Optional" data-id="element-125" />

          <TextArea label="Websites" value={formData.websites} onChange={e => setFormData({
        ...formData,
        websites: e.target.value
      })} placeholder="example.com" helperText="Enter each website on a separate line" rows={3} data-id="element-126" />

          <Input label="Street Address" value={formData.address_street} onChange={e => setFormData({
        ...formData,
        address_street: e.target.value
      })} placeholder="123 Main St" data-id="element-127" />

          <div className="grid grid-cols-3 gap-4" data-id="element-128">
            <Input label="City" value={formData.address_city} onChange={e => setFormData({
          ...formData,
          address_city: e.target.value
        })} placeholder="Detroit" data-id="element-129" />
            <Input label="State" value={formData.address_state} onChange={e => setFormData({
          ...formData,
          address_state: e.target.value
        })} placeholder="MI" data-id="element-130" />
            <Input label="ZIP" value={formData.address_zip} onChange={e => setFormData({
          ...formData,
          address_zip: e.target.value
        })} placeholder="48201" data-id="element-131" />
          </div>

          <Checkbox label="Terms accepted" checked={formData.terms_accepted} onChange={e => setFormData({
        ...formData,
        terms_accepted: e.target.checked
      })} data-id="element-132" />

          {isTKDscore && <Checkbox label="Wholesale Customer" checked={formData.wholesale_customer} onChange={e => setFormData({
        ...formData,
        wholesale_customer: e.target.checked
      })} data-id="element-133" />}

          {formErrors.submit && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-134">
              <p className="text-sm text-red-600" data-id="element-135">{formErrors.submit}</p>
            </div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-136">
            <Button variant="secondary" onClick={onClose} disabled={isSaving} data-id="element-137">
              Cancel
            </Button>
            <Button onClick={handleSubmit} isLoading={isSaving} data-id="element-138">
              {customerId ? 'Save Changes' : 'Add Customer'}
            </Button>
          </div>
        </div>}
    </Modal>;
}